import React, { useCallback, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onReadMoreButtonClicked } from '../../../ai-answer-dialog/on-read-more-button-clicked';
import { onReadMoreButtonShown } from '../../../ai-answer-dialog/on-read-more-button-shown';
import { SainAction } from '../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';

import { messages } from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		paddingTop: token('space.075'),
		paddingInline: token('space.200'),
	},
	readMoreButton: {
		paddingInline: token('space.0'),
		fontWeight: token('font.weight.medium'),
		color: token('color.link'),
		backgroundColor: token('color.background.neutral.subtle'),

		'&:hover': {
			textDecoration: 'underline',
			color: token('color.link.pressed'),
		},
		'&:active': {
			textDecoration: 'underline',
			color: token('color.link.pressed'),
		},
		whiteSpace: 'nowrap',
	},
});

type ReadMoreButtonProps = {
	onClick: () => void;
};

export function ReadMoreButton({ onClick }: ReadMoreButtonProps): React.JSX.Element {
	const [fireAnalyticsEvent] = useAnalytics();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const { commonAttributes } = useAIAnswerContext();

	// Fire analytics event when button is shown
	useEffect(() => {
		fireAnalyticsEvent(onReadMoreButtonShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handle click with analytics
	const handleClick = useCallback(() => {
		fireAnalyticsEvent(onReadMoreButtonClicked(commonAttributes));
		if (!fg('kd_fix_sain_mau_instrumentation')) {
			fireSAINUsageEvent({
				metCriteria: SainAction.READ_MORE_BUTTON_CLICKED,
				commonAttributes,
			});
		}

		onClick();
	}, [fireAnalyticsEvent, onClick, commonAttributes, fireSAINUsageEvent]);

	return (
		<Box xcss={styles.container}>
			<Pressable onClick={handleClick} xcss={styles.readMoreButton} testId="read-more-button">
				<Text size="medium" color="color.link">
					<FormattedMessage {...messages.ai_answer_card_read_more_button} />
				</Text>
			</Pressable>
		</Box>
	);
}
